import type { Cart } from "@dransay/api/models";
import { PRICES } from "@dransay/api/models";
import type { CheckoutStep } from "~/features/checkout/checkout.schemas";
import {
	captureEvent,
	trackBeginCheckout,
} from "~/tooling/telemetry/capture-event";
import { rememberCheckoutTelemetryFlow } from "~/tooling/telemetry/checkout-step-sequence";
import type {
	CheckoutStepCommerceInput,
	TELEMETRY_CHECKOUT_STEP,
} from "~/tooling/telemetry/events";
import {
	checkoutStartedEvent,
	checkoutStepCompletedEvent,
	checkoutStepViewedEvent,
} from "~/tooling/telemetry/events";

const VIEWED_KEY_PREFIX = "telemetry:checkout-step-viewed:";
const COMPLETED_KEY_PREFIX = "telemetry:checkout-step-completed:";
const COMPLETED_CART_KEY_PREFIX = "telemetry:checkout-step-completed-cart:";
const CHECKOUT_STARTED_KEY_PREFIX = "telemetry:checkout-started:";
const PAYMENT_VIEWED_KEY_PREFIX = "telemetry:checkout-step-viewed:payment:";

type OnceClaimId = string | number | null;

/**
 * Steps that fire Viewed once per checkout journey (cartId).
 * Payment uses order-scoped claim at the call site.
 */
const JOURNEY_VIEWED_ONCE_STEPS = new Set<TELEMETRY_CHECKOUT_STEP>([
	"prescription_choice",
	"prescription_upload",
	"diagnose_symptoms",
	"diagnose_treatment",
	"diagnose_cannabis_treatment",
	"diagnose_therapy_effects",
	"diagnose_exclusion_criteria",
	"diagnose_different_medication",
	"awareness",
	"cross_sell_skip_and_continue_to_checkout",
	"cross_sell_continue_to_checkout",
	"delivery_package_1",
	"delivery_package_2",
	"summary",
]);

/** In-memory fallback so Strict Mode / storage failures still once-guard in-realm. */
const claimedKeys = new Set<string>();

function isBrowser(): boolean {
	if (typeof window === "undefined") {
		return false;
	}
	try {
		return typeof sessionStorage !== "undefined";
	} catch {
		// sessionStorage getter can throw (e.g. SecurityError) — still treat as browser.
		return true;
	}
}

function toStorageKeyPart(value: OnceClaimId | undefined): string {
	if (value === null || value === undefined || value === "") {
		return "unknown";
	}
	return String(value);
}

function hasClaimedKey(key: string): boolean {
	if (claimedKeys.has(key)) {
		return true;
	}
	if (!isBrowser()) {
		return false;
	}
	try {
		return sessionStorage.getItem(key) !== null;
	} catch {
		return false;
	}
}

function markClaimedKey(key: string): void {
	claimedKeys.add(key);
	if (!isBrowser()) {
		return;
	}
	try {
		sessionStorage.setItem(key, "1");
	} catch {
		// Memory claim still holds for this JS realm.
	}
}

function claimSessionKey(key: string): boolean {
	if (hasClaimedKey(key)) {
		return false;
	}
	markClaimedKey(key);
	return true;
}

/**
 * Returns true the first time Checkout Started is claimed for this cart.
 * Shared by cart-button click and first checkout layout view.
 */
export function claimCheckoutStartedOnce(cartId?: OnceClaimId): boolean {
	return claimSessionKey(
		`${CHECKOUT_STARTED_KEY_PREFIX}${toStorageKeyPart(cartId)}`,
	);
}

/**
 * Fires GTM begin_checkout + RudderStack Checkout Started once per cartId.
 */
export function captureCheckoutStartedOnce(
	cart: Cart,
	currency: string,
): boolean {
	if (!claimCheckoutStartedOnce(cart.cartId)) {
		return false;
	}
	trackBeginCheckout({
		cart,
		prescriptionValue: PRICES.onlineService,
	});
	captureEvent(checkoutStartedEvent(cart, currency));
	return true;
}

/**
 * Returns true the first time a journey-scoped step is viewed for this cart.
 * Payment always returns true (uses order-scoped claim at the call site).
 *
 * Also treats a prior `unknown` cart claim as already viewed when a real cartId
 * arrives later (avoids double Viewed when cart hydrates mid-diagnose).
 */
export function claimCheckoutStepViewedOnce(
	stepTitle: TELEMETRY_CHECKOUT_STEP,
	cartId?: OnceClaimId,
): boolean {
	if (!JOURNEY_VIEWED_ONCE_STEPS.has(stepTitle)) {
		return true;
	}
	const cartKey = toStorageKeyPart(cartId);
	const specificKey = `${VIEWED_KEY_PREFIX}${stepTitle}:${cartKey}`;

	if (hasClaimedKey(specificKey)) {
		return false;
	}

	if (cartKey !== "unknown") {
		const unknownKey = `${VIEWED_KEY_PREFIX}${stepTitle}:unknown`;
		if (hasClaimedKey(unknownKey)) {
			markClaimedKey(specificKey);
			return false;
		}
	}

	return claimSessionKey(specificKey);
}

/**
 * Builds and pushes Checkout Step Viewed, once per step_title per cartId.
 */
export function captureCheckoutStepViewedOnce(event: {
	step?: CheckoutStep;
	telemetryStep?: TELEMETRY_CHECKOUT_STEP;
	cartId?: OnceClaimId;
	commerce?: CheckoutStepCommerceInput;
}): void {
	if (event.commerce?.flow) {
		rememberCheckoutTelemetryFlow(event.cartId, event.commerce.flow);
	}
	const trackingEvent = checkoutStepViewedEvent(event);
	if (!trackingEvent) {
		return;
	}
	const stepTitle = trackingEvent.properties?.step_title as
		| TELEMETRY_CHECKOUT_STEP
		| undefined;
	if (!stepTitle) {
		return;
	}
	if (!claimCheckoutStepViewedOnce(stepTitle, event.cartId)) {
		return;
	}
	captureEvent(trackingEvent);
}

/**
 * Returns true the first time a Completed event for this step+order is claimed.
 * Prevents duplicate summary Completed when redirect-to remounts.
 */
export function claimCheckoutStepCompletedOnceForOrder(
	stepTitle: TELEMETRY_CHECKOUT_STEP,
	orderId: string | null,
): boolean {
	const orderKey = toStorageKeyPart(orderId);
	return claimSessionKey(`${COMPLETED_KEY_PREFIX}${stepTitle}:${orderKey}`);
}

/**
 * Returns true the first time a Completed event for this step+cart is claimed.
 */
export function claimCheckoutStepCompletedOnceForCart(
	stepTitle: TELEMETRY_CHECKOUT_STEP,
	cartId?: OnceClaimId,
): boolean {
	return claimSessionKey(
		`${COMPLETED_CART_KEY_PREFIX}${stepTitle}:${toStorageKeyPart(cartId)}`,
	);
}

/**
 * Emits Checkout Step Completed once per step_title per cartId.
 */
export function captureCheckoutStepCompletedOnceForCart(event: {
	telemetryStep: TELEMETRY_CHECKOUT_STEP;
	cartId?: OnceClaimId;
	commerce?: CheckoutStepCommerceInput;
}): boolean {
	if (event.commerce?.flow) {
		rememberCheckoutTelemetryFlow(event.cartId, event.commerce.flow);
	}
	if (
		!claimCheckoutStepCompletedOnceForCart(event.telemetryStep, event.cartId)
	) {
		return false;
	}
	captureEvent(
		checkoutStepCompletedEvent(undefined, event.telemetryStep, event.commerce),
	);
	return true;
}

/**
 * Returns true the first time payment Viewed is claimed for this order.
 */
export function claimPaymentStepViewedOnceForOrder(
	orderId: string | null,
): boolean {
	const orderKey = toStorageKeyPart(orderId);
	return claimSessionKey(`${PAYMENT_VIEWED_KEY_PREFIX}${orderKey}`);
}

/** Test-only: clear in-memory claims between specs. */
export function resetCheckoutStepOnceClaimsForTests(): void {
	claimedKeys.clear();
}
